import React, { useState } from 'react'

import _ from 'lodash'
import { Filtro } from '../../utils/Filtro'
import { routes } from '../../constants'
import apiService from '../../services/api.service'
import { Column } from 'primereact/column'
import '../../index.scss'
import LtsLookup from './lts-lookup'
import { InputText } from 'primereact/inputtext'

/**
 * Lookup genérico para tabelas expostas via CRUD dinâmico: consulta
 * POST /lottustech/crud-dinamico/{chave}/all e trabalha com { oid, descricao }.
 *
 * Props específicas:
 * - chave: chave da definição do CRUD dinâmico alvo (ex.: 'crud-poc-relacionamento')
 * - campoExibicao: campo exibido/filtrado (default 'descricao')
 * - camposExibicao: alternativa com várias colunas — vira uma coluna por campo no
 *   dialog e o texto exibido é a concatenação (espelha o concat_ws do backend)
 * - separador: separador da concatenação (default ' - ')
 */
export default function LtsLookupCrudDinamico(props) {
  const [filters, setFilters] = useState([])

  const camposExibicao = _.isEmpty(props.camposExibicao)
    ? [props.campoExibicao || 'descricao']
    : props.camposExibicao
  const separador = props.separador || ' - '
  const campoFiltroExibicao = camposExibicao[0]

  const descricaoDe = (data) =>
    camposExibicao
      .map((campo) => _.get(data, campo, ''))
      .filter((v) => v !== null && v !== undefined && v !== '')
      .join(separador)

  const columns = [
    {
      field: 'oid',
      header: 'Código',
      columnKey: 'oid',
      sortable: false,
      filter: true,
      columnSortField: 'oid',
      visible: true,
    },
    ...camposExibicao.map((campo) => ({
      field: campo,
      header: camposExibicao.length === 1 ? 'Descrição' : _.startCase(campo),
      columnKey: campo,
      columnSortField: campo,
      sortable: true,
      filter: true,
      visible: true,
    })),
  ]

  const loadDataFn = (params) => {
    return apiService.post(`/lottustech/crud-dinamico/${props.chave}/all`, params)
  }

  const onKeyPress = (e) => {
    const value = _.get(props, 'value', null)
    if (value) {
      const filters = [new Filtro(campoFiltroExibicao, value, 'CONTEM')]
      setFilters(filters)
    }
  }

  const onComplete = (event) => {
    const value = event.query
    if (value) {
      const filters = [new Filtro(campoFiltroExibicao, value, 'CONTEM')]
      setFilters(filters)
    }
  }

  const onInputFilter = (e) => {
    const value = e.value
    if (typeof value === 'string') {
      props.onChange({
        oid: '',
        descricao: value,
      })
    } else {
      props.onChange({
        oid: _.get(value, 'oid', ''),
        descricao: descricaoDe(value),
      })
    }
  }

  const autoCompleteTemplate = (data) => {
    return `${data.oid} - ${descricaoDe(data)}`
  }

  const columnFilterElement = (column) => {
    return <InputText style={{ width: '100%' }} name={column.field} onChange={(e) => onChangeTableFilter(column, e)} />
  }

  const onChangeTableFilter = (column, e) => {
    const campo = column.field
    const newFilters = filters.filter((f) => f.campo !== campo)
    const valor = _.get(e, 'target.value', null)
    if (valor) {
      // CONTEM também para o oid: o builder faz ::text ILIKE, e IGUAL_A com texto parcial daria 400
      newFilters.push({ campo, valor, tipoFiltro: 'CONTEM' })
    }
    setFilters(newFilters)
  }

  const onResolvingColumn = () => {
    return columns.map((col) => {
      const { showFilterMenuOptions, showClearButton, showFilterMenu, ...columnProps } = col
      if (col.field === 'oid') {
        return (
          <Column
            {...columnProps}
            key={col.field}
            bodyClassName="column-oid-body-width"
            filterHeaderClassName="column-oid-filter-header-width"
            headerClassName="column-oid-header-width"
            filterElement={columnFilterElement(col)}
            showFilterMenu={false}
            showClearButton={false}
          />
        )
      }
      return (
        <Column
          {...columnProps}
          key={col.field}
          bodyClassName="column-descricao-body-width"
          filterHeaderClassName="column-descricao-filter-header-width"
          headerClassName="column-descricao-header-width"
          filterElement={columnFilterElement(col)}
          showFilterMenu={false}
          showClearButton={false}
        />
      )
    })
  }

  const resolveFilters = (filters) => {
    // o LtsLookup injeta { campo: 'ativo', valor: true } incondicionalmente; no CRUD dinâmico
    // 'ativo' é um campo comum que pode nem estar declarado na definição alvo (o filtro seria
    // rejeitado com 400) — descarta o injetado; filtro do usuário na coluna continua funcionando
    const idxAtivo = filters.findIndex((f) => f && f.campo === 'ativo')
    if (idxAtivo >= 0) {
      filters.splice(idxAtivo, 1)
    }
    const resolveFiltersFn = _.get(props, 'resolveFilters', null)
    if (resolveFiltersFn) {
      resolveFiltersFn.call(resolveFiltersFn, filters)
    }
    return filters
  }

  const onChange = (value) => {
    props.onChange({
      oid: _.get(value, 'oid', ''),
      descricao: descricaoDe(value) || _.get(value, 'descricao', ''),
    })
  }

  return (
    <React.Fragment>
      <LtsLookup
        label={props.label}
        value={props.value}
        name={props.name}
        field={props.field || 'descricao'}
        history={props.history}
        autoCompleteTemplate={autoCompleteTemplate}
        onComplete={onComplete}
        onKeyPress={onKeyPress}
        onInputFilter={onInputFilter}
        pathOnEdit={`${routes.PATH_CRUD_DINAMICO}/${props.chave}`}
        header={props.label}
        columns={columns}
        onResolvingColumn={onResolvingColumn}
        loadDataFn={loadDataFn}
        resolveFilters={resolveFilters}
        onChange={onChange}
        filters={filters}
        setFilters={setFilters}
        editDisabled={props.editDisabled}
        editHidden={props.editHidden}
        multiple={props.multiple}
      />
    </React.Fragment>
  )
}
